export type LineSetInclusionSettings = {
  msWireIncluded?: boolean;
  drainTubingIncluded?: boolean;
};

export type RecommendedAccessoryProduct = {
  productId: number;
  type: string;
};

export type RecommendedAccessoryRec = {
  accessoryProductId: number;
  recommendedPriority: number;
  recommendedQuantity?: number;
  lineSetSettings?: LineSetInclusionSettings;
};

export type RecommendedAccessoryEquipment = {
  equipmentUniqueId: string;
  condenserUniqueId: string;
  recommendations: RecommendedAccessoryRec[];
  selectedAccessories: Array<{ productId: number; quantity: number }>;
  accessoryProducts: Map<number, RecommendedAccessoryProduct>;
};

export type RecommendedAccessoryPanel = {
  accessoryType: { code: string };
  equipment: RecommendedAccessoryEquipment[];
};

export type CartableRecommendedAccessory<TAccessory> = {
  condenserUniqueId: string;
  equipmentUniqueId: string;
  accessory: TAccessory;
  recommendedQuantity: number;
};

export const isZoneEquipmentId = (
  condensers: Array<{ zones: Array<{ uniqueId: string }> }>,
  equipmentUniqueId: string
): boolean =>
  condensers.some((condenser) =>
    condenser.zones.some((zone) => zone.uniqueId === equipmentUniqueId)
  );

export const getLineSetSettingsForEquipment = (
  item: Pick<
    RecommendedAccessoryEquipment,
    'recommendations' | 'accessoryProducts'
  >,
  accessoryTypeCode: string
): LineSetInclusionSettings | undefined => {
  if (accessoryTypeCode !== 'line_set') {
    return undefined;
  }
  const lineSetRec = item.recommendations.find((rec) => {
    if (rec.recommendedPriority !== 1) {
      return false;
    }
    const product = item.accessoryProducts.get(rec.accessoryProductId);
    return product?.type === 'line_set';
  });
  return lineSetRec?.lineSetSettings;
};

export const isAccessoryIncludedWithLineSet = ({
  accessoryType,
  lineSetSettings,
  includedTypes
}: {
  accessoryType: string;
  lineSetSettings?: LineSetInclusionSettings;
  includedTypes?: Set<string>;
}): boolean => {
  if (accessoryType === 'wire' && lineSetSettings?.msWireIncluded) {
    return true;
  }
  if (accessoryType === 'drain_hose' && lineSetSettings?.drainTubingIncluded) {
    return true;
  }
  return includedTypes?.has(accessoryType) ?? false;
};

export const getSelectedRecommendedQuantity = ({
  item,
  accessoryProductId,
  airHandlerAccessories
}: {
  item: Pick<
    RecommendedAccessoryEquipment,
    'equipmentUniqueId' | 'selectedAccessories'
  >;
  accessoryProductId: number;
  airHandlerAccessories: Record<string, Record<number, number>>;
}): number => {
  const fromSelected =
    item.selectedAccessories.find((acc) => acc.productId === accessoryProductId)
      ?.quantity ?? 0;
  const fromZone =
    airHandlerAccessories[item.equipmentUniqueId]?.[accessoryProductId] ?? 0;
  return Math.max(fromSelected, fromZone);
};

export const isRecommendedRecSelected = ({
  item,
  rec,
  accessoryTypeCode,
  includedTypes,
  airHandlerAccessories
}: {
  item: RecommendedAccessoryEquipment;
  rec: RecommendedAccessoryRec;
  accessoryTypeCode: string;
  includedTypes?: Set<string>;
  airHandlerAccessories: Record<string, Record<number, number>>;
}): boolean => {
  const product = item.accessoryProducts.get(rec.accessoryProductId);
  const settings = getLineSetSettingsForEquipment(item, accessoryTypeCode);
  if (
    product &&
    isAccessoryIncludedWithLineSet({
      accessoryType: product.type,
      lineSetSettings: settings,
      includedTypes
    })
  ) {
    return true;
  }
  const needed = rec.recommendedQuantity ?? 1;
  return (
    getSelectedRecommendedQuantity({
      item,
      accessoryProductId: rec.accessoryProductId,
      airHandlerAccessories
    }) >= needed
  );
};

export const areAllRecommendedAccessoriesSelected = ({
  panels,
  hasSelectableLineSets,
  includedAccessoryTypes,
  airHandlerAccessories
}: {
  panels: RecommendedAccessoryPanel[];
  hasSelectableLineSets: boolean;
  includedAccessoryTypes: Map<string, Set<string>>;
  airHandlerAccessories: Record<string, Record<number, number>>;
}): boolean => {
  const panelsToCheck = panels.filter(
    (panel) => panel.accessoryType.code !== 'line_set' || hasSelectableLineSets
  );
  if (panelsToCheck.length === 0) {
    return false;
  }
  return panelsToCheck.every((panel) =>
    panel.equipment.every((item) => {
      const includedTypes = includedAccessoryTypes.get(item.equipmentUniqueId);
      return item.recommendations
        .filter((rec) => rec.recommendedPriority === 1)
        .every((rec) =>
          isRecommendedRecSelected({
            item,
            rec,
            accessoryTypeCode: panel.accessoryType.code,
            includedTypes,
            airHandlerAccessories
          })
        );
    })
  );
};

export const collectCartableRecommendedAccessories = <
  TAccessory extends RecommendedAccessoryProduct
>({
  panels,
  includedAccessoryTypes
}: {
  panels: Array<{
    accessoryType: { code: string };
    equipment: Array<{
      equipmentUniqueId: string;
      condenserUniqueId: string;
      recommendations: RecommendedAccessoryRec[];
      accessoryProducts: Map<number, TAccessory>;
    }>;
  }>;
  includedAccessoryTypes: Map<string, Set<string>>;
}): Array<CartableRecommendedAccessory<TAccessory>> => {
  const result: Array<CartableRecommendedAccessory<TAccessory>> = [];
  panels.forEach((panel) => {
    panel.equipment.forEach((item) => {
      const settings = getLineSetSettingsForEquipment(
        item,
        panel.accessoryType.code
      );
      const includedTypes = includedAccessoryTypes.get(item.equipmentUniqueId);
      item.recommendations.forEach((rec) => {
        if (rec.recommendedPriority === 1) {
          const accessory = item.accessoryProducts.get(rec.accessoryProductId);
          if (
            accessory &&
            !isAccessoryIncludedWithLineSet({
              accessoryType: accessory.type,
              lineSetSettings: settings,
              includedTypes
            })
          ) {
            result.push({
              condenserUniqueId: item.condenserUniqueId,
              equipmentUniqueId: item.equipmentUniqueId,
              accessory,
              recommendedQuantity: rec.recommendedQuantity ?? 1
            });
          }
        }
      });
    });
  });
  return result;
};

export const buildZoneSelectedAccessories = <
  TAcc extends { productId: number }
>({
  zoneAccMap,
  condenserAccessories,
  fallbackByProductId
}: {
  zoneAccMap: Record<number, number>;
  condenserAccessories: TAcc[];
  fallbackByProductId: Map<number, TAcc>;
}): Array<TAcc & { quantity: number }> => {
  const result: Array<TAcc & { quantity: number }> = [];
  Object.entries(zoneAccMap).forEach(([productId, quantity]) => {
    if (quantity > 0) {
      const id = Number(productId);
      const fromCondenser = condenserAccessories.find(
        (acc) => acc.productId === id
      );
      const source = fromCondenser ?? fallbackByProductId.get(id);
      if (source) {
        result.push({ ...source, quantity });
      }
    }
  });
  return result;
};
